import React, { useCallback, useEffect, useState } from "react";
import {
  fetchSourceSubmissionAdminQueue,
  previewSourceSubmissionCard,
  reviewSourceSubmissionAdmin,
} from "../../api/sourceSubmissions";
import SourceSubmissionCardPreview from "./SourceSubmissionCardPreview";

const LLM_CARD_ON_APPROVE_STORAGE_KEY = "albis-admin-approve-llm-card";

function readLlmCardOnApprovePreference() {
  try {
    const v = localStorage.getItem(LLM_CARD_ON_APPROVE_STORAGE_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    /* ignore */
  }
  return false;
}

function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

export default function SourceSubmissionReviewPanel({ userMayUse }) {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [finalDraft, setFinalDraft] = useState({});
  const [rejectDraft, setRejectDraft] = useState({});
  const [busyIds, setBusyIds] = useState({});
  const [approveNotice, setApproveNotice] = useState("");
  const [createLlmCardOnApprove, setCreateLlmCardOnApprove] = useState(
    readLlmCardOnApprovePreference
  );
  /** @type {[Record<string, object>, Function]} */
  const [cardPreviews, setCardPreviews] = useState({});
  const [previewBusyIds, setPreviewBusyIds] = useState({});

  const load = useCallback(async () => {
    if (!userMayUse) return;
    setLoading(true);
    setError("");
    try {
      const list = await fetchSourceSubmissionAdminQueue({ status: filter });
      setItems(list);
      const nextFinal = {};
      const nextReject = {};
      list.forEach((row) => {
        const suggested =
          row.draftPayload?.suggested_url_canonical ||
          row.draftPayload?.suggested_url_raw ||
          row.sourceLink ||
          "";
        nextFinal[row.id] = suggested;
        nextReject[row.id] = "";
      });
      setFinalDraft(nextFinal);
      setRejectDraft(nextReject);
      if (filter !== "pending") {
        setCardPreviews({});
      }
    } catch (e) {
      setItems([]);
      setFinalDraft({});
      setRejectDraft({});
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [userMayUse, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const runPreview = async (rowId) => {
    setError("");
    const url = (finalDraft[rowId] || "").trim();
    if (!url.startsWith("https://")) {
      setError("Enter a valid https URL before generating a preview.");
      return;
    }
    setPreviewBusyIds((b) => ({ ...b, [rowId]: true }));
    try {
      const result = await previewSourceSubmissionCard({ sourceUrl: url });
      const card = result?.card;
      if (!card || !card.title) {
        throw new Error("Preview did not return a card.");
      }
      setCardPreviews((p) => ({
        ...p,
        [rowId]: { ...card, link: result.sourceUrl || url },
      }));
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setPreviewBusyIds((b) => {
        const n = { ...b };
        delete n[rowId];
        return n;
      });
    }
  };

  /** @param {'approve' | 'reject'} action */
  const runReview = async (rowId, action) => {
    setError("");
    setApproveNotice("");
    const needsCard = action === "approve" && createLlmCardOnApprove;
    if (needsCard && !cardPreviews[rowId]) {
      setError(
        "Generate and review the card preview before approving with a card."
      );
      return;
    }

    setBusyIds((b) => ({ ...b, [rowId]: true }));
    try {
      const result = await reviewSourceSubmissionAdmin({
        submissionId: rowId,
        action,
        finalUrl: finalDraft[rowId],
        reason: rejectDraft[rowId],
        ...(action === "approve"
          ? {
              createLlmCardOnApprove,
              ...(needsCard ? { cardDraft: cardPreviews[rowId] } : {}),
            }
          : {}),
      });
      if (action === "approve" && result?.llmCard?.enabled && result.llmCard.title) {
        const verb =
          result.llmCard.action === "updated" ? "Updated" : "Created";
        setApproveNotice(
          `${verb}: "${result.llmCard.title}" (card id ${result.llmCard.userCardId?.slice(0, 8) ?? "—"}…).`
        );
      }
      setCardPreviews((p) => {
        const n = { ...p };
        delete n[rowId];
        return n;
      });
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyIds((b) => {
        const n = { ...b };
        delete n[rowId];
        return n;
      });
    }
  };

  if (!userMayUse) return null;

  return (
    <section
      className="activities-import-source-queue"
      aria-labelledby="activities-source-queue-heading"
    >
      <div className="activities-import-review-head">
        <h2 id="activities-source-queue-heading">Suggested activity sources</h2>
        <div className="activities-import-queue-filters">
          <label>
            Filter{" "}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={loading}
            >
              <option value="pending">Pending</option>
              <option value="completed">Approved</option>
              <option value="failed">Rejected</option>
              <option value="all">All</option>
            </select>
          </label>
          <button type="button" onClick={() => load()} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>
      <p className="activities-import-intro" style={{ marginTop: 0 }}>
        Parents suggest pages with family activities. You can edit the URL before approving.
      </p>
      <div className="activities-import-check">
        <label>
          <input
            type="checkbox"
            checked={createLlmCardOnApprove}
            onChange={(e) => {
              const next = e.target.checked;
              setCreateLlmCardOnApprove(next);
              try {
                localStorage.setItem(
                  LLM_CARD_ON_APPROVE_STORAGE_KEY,
                  next ? "1" : "0"
                );
              } catch {
                /* ignore */
              }
              if (!next) setCardPreviews({});
            }}
          />
          <span>
            On approve, create one activity card (preview and edit before saving).
            Off: save source only for later bulk import.
          </span>
        </label>
      </div>
      {approveNotice ? (
        <p className="activities-import-intro" role="status">
          {approveNotice}
        </p>
      ) : null}
      {error ? (
        <div className="activities-import-error" role="alert">
          {error}
        </div>
      ) : null}
      {loading && items.length === 0 ? (
        <p className="activities-import-intro">Loading…</p>
      ) : null}
      {!loading && items.length === 0 ? (
        <p className="activities-import-intro" style={{ marginBottom: 0 }}>
          No items.
        </p>
      ) : null}
      <ul className="activities-import-source-queue-list">
        {items.map((item) => {
          const busy = !!busyIds[item.id];
          const previewBusy = !!previewBusyIds[item.id];
          const cardPreview = cardPreviews[item.id];
          const suggested =
            item.draftPayload?.suggested_url_raw ||
            item.draftPayload?.suggested_url_canonical ||
            item.sourceLink ||
            "";
          return (
            <li key={item.id} className="activities-import-source-queue-item">
              <div className="activities-import-queue-meta">
                <span>{formatDt(item.createdAt)}</span>
                <span className={`activities-import-queue-status status-${item.status}`}>
                  {item.status === "pending"
                    ? "Pending"
                    : item.status === "completed"
                      ? "Approved"
                      : item.status === "failed"
                        ? "Rejected"
                        : item.status}
                </span>
                <span className="activities-import-queue-id" title={item.userId}>
                  User: {(item.userId || "").slice(0, 8)}…
                </span>
              </div>
              <p className="activities-import-queue-original">
                <strong>Submitted:</strong>{" "}
                <a href={suggested} target="_blank" rel="noopener noreferrer">
                  {suggested}
                </a>
              </p>
              <label className="activities-import-queue-final-label">
                Source URL when approving (editable)
                <input
                  type="url"
                  className="activities-import-queue-url-input"
                  value={finalDraft[item.id] ?? suggested}
                  disabled={busy || item.status !== "pending"}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFinalDraft((d) => ({ ...d, [item.id]: next }));
                    setCardPreviews((p) => {
                      const n = { ...p };
                      delete n[item.id];
                      return n;
                    });
                  }}
                />
              </label>
              {item.status === "failed" && item.error ? (
                <p className="activities-import-queue-reason">
                  Note: {item.error}
                </p>
              ) : null}
              {item.draftPayload?.review?.approved_url_canonical ? (
                <p className="activities-import-queue-meta">
                  Stored as:{" "}
                  <strong>{item.draftPayload.review.approved_url_canonical}</strong>
                </p>
              ) : null}
              {item.status === "pending" && (
                <>
                  {createLlmCardOnApprove ? (
                    <div className="source-card-preview-block">
                      <div className="source-card-preview-toolbar">
                        <button
                          type="button"
                          className="activities-import-run-btn-secondary"
                          onClick={() => runPreview(item.id)}
                          disabled={busy || previewBusy}
                        >
                          {previewBusy
                            ? "Generating preview…"
                            : cardPreview
                              ? "Regenerate preview"
                              : "Generate card preview"}
                        </button>
                        {cardPreview ? (
                          <span className="source-card-preview-ready">
                            Preview ready — edit below, then approve.
                          </span>
                        ) : null}
                      </div>
                      {cardPreview ? (
                        <SourceSubmissionCardPreview
                          card={cardPreview}
                          disabled={busy || previewBusy}
                          onChange={(next) =>
                            setCardPreviews((p) => ({
                              ...p,
                              [item.id]: next,
                            }))
                          }
                        />
                      ) : null}
                    </div>
                  ) : null}
                  <label className="activities-import-queue-reject-note">
                    Reason if rejecting (optional)
                    <input
                      type="text"
                      className="activities-import-queue-reject-input"
                      value={rejectDraft[item.id] ?? ""}
                      disabled={busy}
                      onChange={(e) =>
                        setRejectDraft((d) => ({ ...d, [item.id]: e.target.value }))
                      }
                    />
                  </label>
                  <div className="activities-import-actions" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() => runReview(item.id, "approve")}
                      disabled={
                        busy ||
                        (createLlmCardOnApprove && !cardPreview)
                      }
                    >
                      {createLlmCardOnApprove
                        ? "Approve source & publish card"
                        : "Approve"}
                    </button>
                    <button
                      type="button"
                      onClick={() => runReview(item.id, "reject")}
                      disabled={busy}
                    >
                      Reject
                    </button>
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
